/*
 * GVS collection buying guide disclosure.
 * One real button controls one long-form panel. Media remains independent.
 * Event delegation keeps the interaction working after Shopify section/filter refreshes.
 *
 * Launch hardening: collection descriptions can contain legacy rich-text wrappers around
 * the SPLIT marker. The Liquid source order protects media, while the post-SPLIT rich HTML
 * is stored as escaped template text and hydrated inside its own target element. That keeps
 * browser HTML repair local and prevents malformed legacy fragments from breaking Shopify's
 * section wrapper or triggering Theme Editor broken-HTML warnings.
 */
(() => {
  const TOGGLE_SELECTOR = '[data-gvs-collection-guide-toggle]';
  const ROOT_SELECTOR = '[data-gvs-collection-guide]';
  const SHELL_SELECTOR = '[data-gvs-collection-guide-shell]';
  const MEDIA_SELECTOR = '[data-gvs-collection-guide-media]';
  const RICHTEXT_SELECTOR = '[data-gvs-collection-guide-richtext]';
  const SOURCE_SELECTOR = 'template[data-gvs-collection-guide-source]';
  const BOUND_FLAG = '__gvsCollectionGuideDisclosureBound';

  if (window[BOUND_FLAG]) return;
  window[BOUND_FLAG] = true;

  const getScopedElements = (scope, selector) => {
    if (!scope) return [];

    const elements = [];
    if (scope.matches?.(selector)) elements.push(scope);
    elements.push(...Array.from(scope.querySelectorAll?.(selector) || []));

    if (!elements.length && scope !== document) {
      const closestSection = scope.closest?.('.shopify-section');
      if (closestSection) elements.push(...closestSection.querySelectorAll(selector));
    }

    return elements;
  };

  const hydrateGuideRichText = (scope = document) => {
    const targets = getScopedElements(scope, RICHTEXT_SELECTOR);
    if (!targets.length) return;

    const sources = Array.from(document.querySelectorAll(SOURCE_SELECTOR));

    for (const target of targets) {
      if (target.dataset.gvsCollectionGuideHydrated === 'true') continue;

      const sectionId = target.dataset.gvsCollectionGuideRichtext;
      if (!sectionId) continue;

      const source = sources.find(
        (candidate) => candidate.dataset.gvsCollectionGuideSource === sectionId
      );
      if (!source) continue;

      const richHtml = source.content?.textContent || '';
      if (!richHtml.trim()) continue;

      // innerHTML parses in the target element's own context. Any legacy unmatched closing
      // tags are repaired/ignored locally instead of being able to close Shopify wrappers.
      target.innerHTML = richHtml;
      target.dataset.gvsCollectionGuideHydrated = 'true';
    }
  };

  const repairMediaPlacement = (scope = document) => {
    const shells = scope.matches?.(SHELL_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(SHELL_SELECTOR) || []);

    if (!shells.length && scope !== document) {
      const closestSection = scope.closest?.('.shopify-section');
      if (closestSection) {
        shells.push(...closestSection.querySelectorAll(SHELL_SELECTOR));
      }
    }

    const allMedia = Array.from(document.querySelectorAll(MEDIA_SELECTOR));

    for (const shell of shells) {
      const sectionId = shell.dataset.gvsCollectionGuideShell;
      if (!sectionId) continue;

      const media = allMedia.find(
        (candidate) => candidate.dataset.gvsCollectionGuideMedia === sectionId
      );

      if (media && media.parentElement !== shell) {
        shell.prepend(media);
      }
    }
  };

  const initialiseGuide = (scope = document) => {
    hydrateGuideRichText(scope);
    repairMediaPlacement(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseGuide(), { once: true });
  } else {
    initialiseGuide();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialiseGuide(event.target);
  });

  // Horizon filter/sort updates morph the collection section without firing
  // Shopify's Theme Editor section event. Rehydrate legacy fragments if needed.
  document.addEventListener('gvs:collection-render-end', () => {
    initialiseGuide();
  });

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest(TOGGLE_SELECTOR);
    if (!toggle) return;

    const panelId = toggle.getAttribute('aria-controls');
    if (!panelId) return;

    const panel = document.getElementById(panelId);
    if (!panel) return;

    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
    const nextOpen = !isOpen;

    toggle.setAttribute('aria-expanded', String(nextOpen));
    panel.hidden = !nextOpen;

    const root = toggle.closest(ROOT_SELECTOR);
    if (root) root.dataset.open = String(nextOpen);
  });
})();
